import React from 'react';
import { Link, type Href } from 'expo-router';
import { Platform, StyleSheet, Text, TouchableOpacity, View, type StyleProp, type ViewStyle } from 'react-native';
import colors from '../../lib/colors';
import { interMedium, interRegular, interSemiBold } from '../../lib/fonts';
import { type LocalizedMessage, useResolvedMessage } from '../../lib/i18n/messages';

interface SectionProps {
    backgroundColor?: string;
    bottom?: number;
    children: React.ReactNode;
    isNarrow: boolean;
    nativeID?: string;
    top?: number;
}

export function Section({
    backgroundColor = '#ffffff',
    bottom = 80,
    children,
    isNarrow,
    nativeID,
    top = 80,
}: SectionProps) {
    return (
        <View
            nativeID={nativeID}
            style={[
                styles.section,
                {
                    backgroundColor,
                    paddingBottom: isNarrow ? Math.min(bottom, 64) : bottom,
                    paddingTop: isNarrow ? Math.min(top, 64) : top,
                },
            ]}
        >
            <View style={[styles.sectionInner, isNarrow && styles.sectionInnerNarrow]}>
                {children}
            </View>
        </View>
    );
}

export function SectionLabel({
    children,
    fontsLoaded,
    light = false,
}: {
    children: React.ReactNode;
    fontsLoaded: boolean;
    light?: boolean;
}) {
    return (
        <View style={styles.sectionLabelRow}>
            <View style={[styles.sectionLabelRule, light && styles.sectionLabelRuleLight]} />
            <Text style={[styles.sectionLabel, light && styles.sectionLabelLight, fontsLoaded && interSemiBold]}>
                {children}
            </Text>
        </View>
    );
}

export function Heading({
    children,
    fontsLoaded,
    level = 2,
    light = false,
    size = 'large',
}: {
    children: React.ReactNode;
    fontsLoaded: boolean;
    level?: 1 | 2 | 3;
    light?: boolean;
    size?: 'hero' | 'large' | 'medium' | 'small';
}) {
    return (
        <Text
            style={[
                styles.heading,
                size === 'hero' && styles.headingHero,
                size === 'medium' && styles.headingMedium,
                size === 'small' && styles.headingSmall,
                light && styles.headingLight,
                fontsLoaded && interSemiBold,
            ]}
            accessibilityRole="header"
            {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': level })}
        >
            {children}
        </Text>
    );
}

export function Body({
    children,
    fontsLoaded,
    light = false,
}: {
    children: React.ReactNode;
    fontsLoaded: boolean;
    light?: boolean;
}) {
    return (
        <Text style={[styles.body, light && styles.bodyLight, fontsLoaded && interRegular]}>
            {children}
        </Text>
    );
}

export function PrimaryButton({
    children,
    fontsLoaded,
    href,
    lightSubdued = false,
    onPress,
    subdued = false,
    style,
}: {
    children: React.ReactNode;
    fontsLoaded: boolean;
    href?: Href;
    lightSubdued?: boolean;
    onPress?: () => void;
    subdued?: boolean;
    style?: StyleProp<ViewStyle>;
}) {
    const buttonStyle = [
        styles.ctaButton,
        subdued && styles.ctaButtonSubdued,
        lightSubdued && styles.ctaButtonSubduedLight,
    ];
    const buttonTextStyle = [
        styles.ctaButtonText,
        subdued && styles.ctaButtonTextSubdued,
        lightSubdued && styles.ctaButtonTextSubduedLight,
        fontsLoaded && interSemiBold,
    ];

    if (href) {
        return (
            <Link
                href={href}
                style={[
                    buttonStyle,
                    style,
                    buttonTextStyle,
                    Platform.OS === 'web' && styles.ctaButtonLink,
                ]}
            >
                {children}
            </Link>
        );
    }

    return (
        <TouchableOpacity
            activeOpacity={0.86}
            onPress={onPress}
            style={[buttonStyle, style]}
        >
            <Text style={buttonTextStyle}>
                {children}
            </Text>
        </TouchableOpacity>
    );
}

export function StepCard({
    badge,
    children,
    fontsLoaded,
    number,
    title,
}: {
    badge: LocalizedMessage;
    children: React.ReactNode;
    fontsLoaded: boolean;
    number: string;
    title: LocalizedMessage;
}) {
    const resolvedBadge = useResolvedMessage(badge);
    const resolvedTitle = useResolvedMessage(title);
    return (
        <View style={styles.stepCard}>
            <View style={styles.stepCardTop}>
                <Text style={[styles.stepCardNumber, fontsLoaded && interMedium]}>{number}</Text>
                <Text style={[styles.stepBadge, fontsLoaded && interSemiBold]}>{resolvedBadge}</Text>
            </View>
            <Text style={[styles.stepCardTitle, fontsLoaded && interSemiBold]}>{resolvedTitle}</Text>
            <Text style={[styles.stepCardBody, fontsLoaded && interRegular]}>{children}</Text>
        </View>
    );
}

export function FeatureTile({
    children,
    fontsLoaded,
    icon,
    title,
    wide,
}: {
    children: React.ReactNode;
    fontsLoaded: boolean;
    icon: string;
    title: LocalizedMessage;
    wide: boolean;
}) {
    const resolvedTitle = useResolvedMessage(title);
    return (
        <View style={[styles.featureTile, wide && styles.featureTileWide]}>
            <Text style={[styles.featureIcon, fontsLoaded && interMedium]}>{icon}</Text>
            <Text style={[styles.featureTitle, fontsLoaded && interSemiBold]}>{resolvedTitle}</Text>
            <Text style={[styles.featureBody, fontsLoaded && interRegular]}>{children}</Text>
        </View>
    );
}

const styles = StyleSheet.create({
    section: {
        width: '100%',
    },
    sectionInner: {
        alignSelf: 'center',
        maxWidth: 1100,
        paddingHorizontal: 32,
        width: '100%',
    },
    sectionInnerNarrow: {
        paddingHorizontal: 20,
    },
    sectionLabelRow: {
        alignItems: 'center',
        flexDirection: 'row',
        gap: 10,
        marginBottom: 16,
    },
    sectionLabelRule: {
        backgroundColor: colors.primaryGreen,
        height: 2,
        width: 20,
    },
    sectionLabelRuleLight: {
        backgroundColor: colors.primaryGreen,
    },
    sectionLabel: {
        color: colors.primaryGreen,
        fontSize: 12,
        fontWeight: '700',
        textTransform: 'uppercase',
    },
    sectionLabelLight: {
        color: colors.primaryGreenLight,
    },
    heading: {
        color: colors.text900,
        fontSize: 40,
        fontWeight: '800',
        lineHeight: 46,
        marginBottom: 0,
    },
    headingHero: {
        fontSize: 48,
        lineHeight: 57,
    },
    headingMedium: {
        fontSize: 32,
        lineHeight: 38,
    },
    headingSmall: {
        fontSize: 28,
        lineHeight: 34,
    },
    headingLight: {
        color: '#ffffff',
    },
    body: {
        color: colors.text600,
        fontSize: 17,
        lineHeight: 29,
        marginTop: 20,
    },
    bodyLight: {
        color: 'rgba(255,255,255,0.66)',
    },
    ctaButton: {
        alignItems: 'center',
        backgroundColor: colors.action,
        borderRadius: 10,
        justifyContent: 'center',
        minHeight: 44,
        paddingHorizontal: 24,
        paddingVertical: 12,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'opacity 0.2s',
        }),
    },
    ctaButtonLink: {
        display: 'flex',
        textDecorationLine: 'none',
    },
    ctaButtonSubdued: {
        backgroundColor: 'transparent',
        borderColor: colors.primaryGreen,
        borderWidth: 1.5,
    },
    ctaButtonSubduedLight: {
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderColor: 'rgba(255,255,255,0.18)',
    },
    ctaButtonText: {
        color: '#ffffff',
        fontSize: 15,
        fontWeight: '700',
        textAlign: 'center',
    },
    ctaButtonTextSubdued: {
        color: colors.primaryGreen,
        fontWeight: '600',
    },
    ctaButtonTextSubduedLight: {
        color: '#ffffff',
    },
    stepCard: {
        backgroundColor: colors.card,
        borderColor: colors.border,
        borderRadius: 16,
        borderWidth: 1.5,
        flex: 1,
        paddingHorizontal: 30,
        paddingVertical: 36,
    },
    stepCardTop: {
        alignItems: 'flex-start',
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 24,
    },
    stepCardNumber: {
        color: colors.border,
        fontSize: 32,
        fontWeight: '600',
        lineHeight: 34,
    },
    stepBadge: {
        backgroundColor: `${colors.primaryGreen}1a`,
        borderRadius: 20,
        color: colors.primaryGreenDark,
        fontSize: 11,
        fontWeight: '700',
        paddingHorizontal: 10,
        paddingVertical: 4,
        textTransform: 'uppercase',
    },
    stepCardTitle: {
        color: colors.text900,
        fontSize: 19,
        fontWeight: '700',
        lineHeight: 25,
        marginBottom: 12,
    },
    stepCardBody: {
        color: colors.text600,
        fontSize: 15,
        lineHeight: 24,
    },
    featureTile: {
        borderColor: colors.border,
        borderRadius: 14,
        borderWidth: 1.5,
        minHeight: 190,
        paddingHorizontal: 24,
        paddingVertical: 28,
        width: '23.5%',
    },
    featureTileWide: {
        width: '100%',
    },
    featureIcon: {
        color: colors.primaryGreen,
        fontSize: 20,
        lineHeight: 22,
        marginBottom: 18,
    },
    featureTitle: {
        color: colors.text900,
        fontSize: 15,
        fontWeight: '700',
        lineHeight: 20,
        marginBottom: 10,
    },
    featureBody: {
        color: colors.text600,
        fontSize: 14,
        lineHeight: 22,
    },
});

export const primitiveStyles = styles;
